import React, { useState } from 'react';
import { Filter, X, ChevronDown, Check } from 'lucide-react';

/**
 * FilterBar - Horizontal filter strip with multiple filter types
 *
 * Props:
 *  - filters: Array<{
 *      key: string,
 *      label: string,
 *      type: 'select' | 'multi' | 'toggle',
 *      options?: Array<{ label, value }>,
 *      value: any,
 *      onChange: (value) => void,
 *    }>
 *  - onReset: () => void
 *  - showResetBtn: boolean
 *  - className: string
 *  - id: string
 */
export default function FilterBar({
  filters = [],
  onReset,
  showResetBtn = true,
  className = '',
  id = 'filter-bar',
}) {
  const hasActiveFilter = filters.some(f => {
    if (Array.isArray(f.value)) return f.value.length > 0;
    return f.value && f.value !== 'All' && f.value !== '';
  });

  return (
    <div className={`ui-filter-bar ${className}`} id={id} role="group" aria-label="Filter options">
      <span className="ui-filter-bar-label">
        <Filter size={14} />
        Filters
      </span>

      <div className="ui-filter-bar-chips">
        {filters.map(filter => (
          <FilterChip key={filter.key} filter={filter} />
        ))}
      </div>

      {showResetBtn && hasActiveFilter && (
        <button
          type="button"
          className="ui-filter-reset-btn"
          onClick={onReset}
          aria-label="Reset all filters"
          id={`${id}-reset`}
        >
          <X size={13} />
          Reset
        </button>
      )}
    </div>
  );
}

function FilterChip({ filter }) {
  const [isOpen, setIsOpen] = useState(false);

  const isActive = Array.isArray(filter.value)
    ? filter.value.length > 0
    : filter.value && filter.value !== 'All' && filter.value !== '';

  const displayValue = () => {
    if (filter.type === 'toggle') {
      return filter.value ? filter.label : null;
    }
    if (filter.type === 'multi') {
      if (!Array.isArray(filter.value) || filter.value.length === 0) return null;
      if (filter.value.length === 1) {
        const opt = filter.options?.find(o => o.value === filter.value[0]);
        return opt?.label ?? filter.value[0];
      }
      return `${filter.value.length} selected`;
    }
    if (!filter.value || filter.value === 'All') return null;
    const opt = filter.options?.find(o => o.value === filter.value);
    return opt?.label ?? filter.value;
  };

  const label = displayValue();

  const handleToggle = () => {
    if (filter.type === 'toggle') {
      filter.onChange(!filter.value);
    } else {
      setIsOpen(o => !o);
    }
  };

  const handleSelect = (val) => {
    if (filter.type === 'multi') {
      const arr = Array.isArray(filter.value) ? filter.value : [];
      const next = arr.includes(val) ? arr.filter(v => v !== val) : [...arr, val];
      filter.onChange(next);
    } else {
      filter.onChange(val);
      setIsOpen(false);
    }
  };

  const handleClear = (e) => {
    e.stopPropagation();
    if (filter.type === 'multi') filter.onChange([]);
    else if (filter.type === 'toggle') filter.onChange(false);
    else filter.onChange('All');
  };

  return (
    <div className={`ui-filter-chip ${isActive ? 'active' : ''}`}>
      <button
        type="button"
        className="ui-filter-chip-trigger"
        onClick={handleToggle}
        aria-expanded={filter.type !== 'toggle' ? isOpen : undefined}
        aria-pressed={filter.type === 'toggle' ? filter.value : undefined}
        id={`filter-${filter.key}`}
      >
        <span className="ui-filter-chip-label">{filter.label}</span>
        {label && <span className="ui-filter-chip-value">: {label}</span>}
        {isActive && filter.type !== 'toggle' && (
          <span className="ui-filter-chip-clear" onClick={handleClear} aria-label={`Clear ${filter.label} filter`}>
            <X size={11} />
          </span>
        )}
        {filter.type !== 'toggle' && (
          <ChevronDown size={12} className={`ui-filter-chip-chevron ${isOpen ? 'open' : ''}`} />
        )}
      </button>

      {isOpen && filter.type !== 'toggle' && (
        <>
          <div className="ui-filter-chip-backdrop" onClick={() => setIsOpen(false)} />
          <div className="ui-filter-chip-menu" role="listbox">
            {filter.type === 'select' && (
              <>
                <button
                  type="button"
                  className={`ui-filter-chip-option ${(!filter.value || filter.value === 'All') ? 'selected' : ''}`}
                  onClick={() => handleSelect('All')}
                  role="option"
                >
                  All
                  {(!filter.value || filter.value === 'All') && <Check size={12} />}
                </button>
                {filter.options?.map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    className={`ui-filter-chip-option ${filter.value === opt.value ? 'selected' : ''}`}
                    onClick={() => handleSelect(opt.value)}
                    role="option"
                    aria-selected={filter.value === opt.value}
                  >
                    {opt.label}
                    {filter.value === opt.value && <Check size={12} />}
                  </button>
                ))}
              </>
            )}

            {filter.type === 'multi' && (
              <>
                {filter.options?.map(opt => {
                  const checked = Array.isArray(filter.value) && filter.value.includes(opt.value);
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      className={`ui-filter-chip-option multi ${checked ? 'selected' : ''}`}
                      onClick={() => handleSelect(opt.value)}
                      role="option"
                      aria-selected={checked}
                    >
                      <span className={`ui-filter-chip-checkbox ${checked ? 'checked' : ''}`}>
                        {checked && <Check size={10} />}
                      </span>
                      {opt.label}
                    </button>
                  );
                })}
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}
